import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  TextField, Button, Box, Typography, Container, Autocomplete, CircularProgress,
  Modal, List, ListItemButton, ListItemText, Divider, FormControlLabel, Switch
} from '@mui/material';
import { debounce } from 'lodash';
import PessoaForm from './PessoaForm';
import FormCard from './FormCard';
import { modalSurfaceSx } from '../styles/uiTokens';

const toDateInput = (value) => (value ? String(value).split('T')[0] : '');

function AcessoLiberadoForm({ onSaved, initialData, onCancelEdit }) {
  const { user } = useAuth();
  const [id, setId] = useState(null);
  const [empresa_id, setEmpresaId] = useState('');
  const [imovel_id, setImovelId] = useState('');
  const [pessoa_id, setPessoaId] = useState('');
  const [autorizador_id, setAutorizadorId] = useState('');
  const [data_inicio, setDataInicio] = useState('');
  const [data_fim, setDataFim] = useState('');
  const [observacao, setObservacao] = useState('');
  const [ativo, setAtivo] = useState(true);

  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [imovelOptions, setImovelOptions] = useState([]);
  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [autorizadorOptions, setAutorizadorOptions] = useState([]);

  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [selectedImovel, setSelectedImovel] = useState(null);
  const [selectedPessoa, setSelectedPessoa] = useState(null);
  const [selectedAutorizador, setSelectedAutorizador] = useState(null);
  const [empresaInputValue, setEmpresaInputValue] = useState('');
  const [pessoaInputValue, setPessoaInputValue] = useState('');

  const [openPessoaModal, setOpenPessoaModal] = useState(false);
  const [openMoradoresModal, setOpenMoradoresModal] = useState(false);
  const [moradoresList, setMoradoresList] = useState([]);
  const [loadingMoradores, setLoadingMoradores] = useState(false);

  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [loadingPessoas, setLoadingPessoas] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleOpenPessoaModal = () => setOpenPessoaModal(true);
  const handleClosePessoaModal = () => setOpenPessoaModal(false);

  const handleOpenMoradoresModal = () => {
    if (!imovel_id) return;
    setLoadingMoradores(true);
    setOpenMoradoresModal(true);
    api.get(`/moradores?imovelId=${imovel_id}&limit=100`)
      .then((response) => setMoradoresList(response.data.data || response.data))
      .catch((error) => console.error('Erro ao buscar moradores:', error))
      .finally(() => setLoadingMoradores(false));
  };
  const handleCloseMoradoresModal = () => setOpenMoradoresModal(false);

  const handleSelectMorador = (morador) => {
    const pessoa = { id: morador.pessoa_id, nome: morador.pessoa_nome };
    setSelectedAutorizador(pessoa);
    setAutorizadorId(pessoa.id);
    setAutorizadorOptions([pessoa]);
    setOpenMoradoresModal(false);
  };

  const modalContainerSx = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minHeight: '100dvh',
    py: { xs: 1, sm: 2 },
    px: { xs: 1, sm: 2 },
    overflowY: 'auto',
  };

  const modalContentSx = {
    width: { xs: '100%', sm: 520 },
    maxWidth: '100%',
    mt: { xs: 0, sm: 2 },
    maxHeight: 'calc(100dvh - 32px)',
    overflowY: 'auto',
    p: { xs: 2, sm: 4 },
    ...modalSurfaceSx,
  };

  const debouncedFetchEmpresas = useMemo(() => debounce((searchTerm) => {
    setLoadingEmpresas(true);
    api.get(`/empresas?search=${searchTerm}`)
      .then((response) => setEmpresaOptions(response.data.data || response.data))
      .catch((error) => console.error('Erro ao buscar empresas:', error))
      .finally(() => setLoadingEmpresas(false));
  }, 500), []);

  const debouncedFetchImoveis = useMemo(() => debounce((searchTerm) => {
    setLoadingImoveis(true);
    api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
      .then((response) => setImovelOptions(response.data.data || response.data))
      .catch((error) => console.error('Erro ao buscar imoveis:', error))
      .finally(() => setLoadingImoveis(false));
  }, 500), []);

  const debouncedFetchPessoas = useMemo(() => debounce((searchTerm, setter) => {
    setLoadingPessoas(true);
    api.get(`/pessoas?search=${encodeURIComponent(searchTerm)}`)
      .then((response) => setter(response.data.data || response.data))
      .catch((error) => console.error('Erro ao buscar pessoas:', error))
      .finally(() => setLoadingPessoas(false));
  }, 500), []);

  useEffect(() => () => {
    debouncedFetchEmpresas.cancel();
    debouncedFetchImoveis.cancel();
    debouncedFetchPessoas.cancel();
  }, [debouncedFetchEmpresas, debouncedFetchImoveis, debouncedFetchPessoas]);

  useEffect(() => {
    const fetchAndSetSelected = async (value, apiPath, setSelectedState, setOptionsState) => {
      if (!value) {
        setSelectedState(null);
        return;
      }
      try {
        const response = await api.get(`${apiPath}?id=${value}`);
        let item = null;
        if (Array.isArray(response.data.data)) item = response.data.data[0];
        else if (response.data.data) item = response.data.data;
        else if (Array.isArray(response.data)) item = response.data[0];
        else if (response.data) item = response.data;
        if (item) {
          setSelectedState(item);
          setOptionsState([item]);
        }
      } catch (error) {
        console.error(`Erro ao buscar item ${value} de ${apiPath}:`, error);
      }
    };

    if (initialData) {
      setId(initialData.id);
      setEmpresaId(initialData.empresa_id);
      setImovelId(initialData.imovel_id);
      setPessoaId(initialData.pessoa_id || '');
      setAutorizadorId(initialData.autorizador_id || '');
      setDataInicio(toDateInput(initialData.data_inicio));
      setDataFim(toDateInput(initialData.data_fim));
      setObservacao(initialData.observacao || '');
      setAtivo(initialData.ativo === 0 || initialData.ativo === false ? false : true);

      fetchAndSetSelected(initialData.imovel_id, '/imoveis', setSelectedImovel, setImovelOptions);
      fetchAndSetSelected(initialData.pessoa_id, '/pessoas', setSelectedPessoa, setPessoaOptions);
      fetchAndSetSelected(initialData.autorizador_id, '/pessoas', setSelectedAutorizador, setAutorizadorOptions);
      if (user?.tipo_usuario === 'admin') {
        fetchAndSetSelected(initialData.empresa_id, '/empresas', setSelectedEmpresa, setEmpresaOptions);
      }
    } else {
      clearForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, user]);

  const clearForm = () => {
    setId(null);
    setEmpresaId(user?.empresa_id || '');
    setImovelId('');
    setPessoaId('');
    setAutorizadorId('');
    setDataInicio('');
    setDataFim('');
    setObservacao('');
    setAtivo(true);
    setSelectedEmpresa(null);
    setEmpresaInputValue('');
    setSelectedImovel(null);
    setSelectedPessoa(null);
    setPessoaInputValue('');
    setSelectedAutorizador(null);
    setImovelOptions([]);
    setPessoaOptions([]);
    setAutorizadorOptions([]);
    setErrorMsg('');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMsg('');

    if (!imovel_id) return setErrorMsg('Selecione o imóvel.');
    if (!pessoa_id) return setErrorMsg('Selecione a pessoa.');
    if (!autorizador_id) return setErrorMsg('Selecione o autorizador.');

    const payload = {
      empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id,
      imovel_id,
      pessoa_id,
      autorizador_id,
      data_inicio: data_inicio || null,
      data_fim: data_fim || null,
      observacao: observacao || null,
      ativo,
    };

    setSaving(true);
    const apiCall = id ? api.put(`/acessos-liberados/${id}`, payload) : api.post('/acessos-liberados', payload);

    apiCall
      .then(() => {
        clearForm();
        onSaved();
        if (id) onCancelEdit();
      })
      .catch((error) => {
        console.error('Erro ao salvar acesso liberado:', error);
        const msg = error?.response?.data?.message
          || (typeof error?.response?.data === 'string' ? error.response.data : null)
          || 'Erro ao salvar acesso liberado.';
        setErrorMsg(msg);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Container maxWidth="lg">
      <FormCard component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 1280 }}>
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Acesso Liberado' : 'Adicionar Novo Acesso Liberado'}
        </Typography>

        {user?.tipo_usuario === 'admin' && (
          <Autocomplete
            id="empresa-autocomplete"
            options={empresaOptions}
            getOptionLabel={(option) => option.nome || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedEmpresa}
            onChange={(event, newValue) => {
              setSelectedEmpresa(newValue);
              setEmpresaId(newValue ? newValue.id : '');
              setEmpresaInputValue(newValue?.nome || '');
            }}
            inputValue={empresaInputValue}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input' || reason === 'clear') {
                setEmpresaInputValue(newInputValue);
                if (newInputValue) debouncedFetchEmpresas(newInputValue);
                else setEmpresaOptions([]);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                variant="outlined"
                margin="normal"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingEmpresas ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={!!initialData}
          />
        )}

        <Autocomplete
          id="imovel-autocomplete"
          options={imovelOptions}
          getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedImovel}
          onChange={(event, newValue) => {
            setSelectedImovel(newValue);
            setImovelId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) debouncedFetchImoveis(newInputValue);
              else setImovelOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Imóvel (unidade)"
              variant="outlined"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          id="pessoa-autocomplete"
          options={pessoaOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedPessoa}
          onChange={(event, newValue) => {
            setSelectedPessoa(newValue);
            setPessoaId(newValue ? newValue.id : '');
            setPessoaInputValue(newValue?.nome || '');
          }}
          inputValue={pessoaInputValue}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input' || reason === 'clear') {
              setPessoaInputValue(newInputValue);
              if (newInputValue) debouncedFetchPessoas(newInputValue, setPessoaOptions);
              else setPessoaOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pessoa com acesso liberado"
              variant="outlined"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    <Button onClick={handleOpenPessoaModal} size="small" sx={{ ml: 1 }}>
                      Cadastrar
                    </Button>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          id="autorizador-autocomplete"
          options={autorizadorOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedAutorizador}
          onChange={(event, newValue) => {
            setSelectedAutorizador(newValue);
            setAutorizadorId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) debouncedFetchPessoas(newInputValue, setAutorizadorOptions);
              else setAutorizadorOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Autorizador (morador responsável)"
              variant="outlined"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    <Button onClick={handleOpenMoradoresModal} size="small" sx={{ ml: 1 }} disabled={!imovel_id}>
                      Moradores
                    </Button>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          label="Data inicial da autorização"
          variant="outlined"
          fullWidth
          type="date"
          value={data_inicio}
          onChange={(e) => setDataInicio(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          helperText="Se vazio, usa a data de hoje."
        />
        <TextField
          label="Data final da autorização"
          variant="outlined"
          fullWidth
          type="date"
          value={data_fim}
          onChange={(e) => setDataFim(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          helperText="Deixe vazio para acesso por tempo indeterminado."
        />

        {id && (
          <FormControlLabel
            control={<Switch checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />}
            label="Acesso ativo"
            sx={{ mt: 1, display: 'block' }}
          />
        )}

        <TextField
          label="Observação"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          margin="normal"
        />

        {errorMsg && (
          <Typography color="error" sx={{ mt: 1 }}>{errorMsg}</Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 2, mr: 2 }}
          disabled={saving}
        >
          {id ? 'Atualizar Acesso Liberado' : 'Adicionar Acesso Liberado'}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={onCancelEdit}
        >
          Cancelar
        </Button>
      </FormCard>

      <Modal
        open={openPessoaModal}
        onClose={handleClosePessoaModal}
        aria-labelledby="modal-title-pessoa"
      >
        <Box sx={modalContainerSx}>
          <Box sx={modalContentSx}>
            <Typography id="modal-title-pessoa" variant="h6" component="h2">
              Cadastrar Nova Pessoa
            </Typography>
            <PessoaForm
              onPessoaAdded={(newPessoa) => {
                handleClosePessoaModal();
                setSelectedPessoa(newPessoa);
                setPessoaId(newPessoa.id);
                setPessoaOptions((prev) => (prev.some((p) => p.id === newPessoa.id) ? prev : [...prev, newPessoa]));
              }}
              onCancelEdit={handleClosePessoaModal}
              initialData={null}
            />
          </Box>
        </Box>
      </Modal>

      <Modal
        open={openMoradoresModal}
        onClose={handleCloseMoradoresModal}
        aria-labelledby="modal-title-moradores"
      >
        <Box sx={modalContainerSx}>
          <Box sx={modalContentSx}>
            <Typography id="modal-title-moradores" variant="h6" component="h2" sx={{ mb: 2 }}>
              Moradores da Unidade
            </Typography>
            {loadingMoradores ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : moradoresList.length === 0 ? (
              <Typography color="text.secondary">Nenhum morador encontrado nesta unidade.</Typography>
            ) : (
              <List disablePadding>
                {moradoresList.map((morador, index) => (
                  <React.Fragment key={morador.id}>
                    {index > 0 && <Divider />}
                    <ListItemButton onClick={() => handleSelectMorador(morador)}>
                      <ListItemText
                        primary={morador.pessoa_nome}
                        secondary={morador.imovel_descricao ? `${morador.imovel_descricao} - ${morador.imovel_numero}` : null}
                      />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Modal>
    </Container>
  );
}

export default AcessoLiberadoForm;
